// Frontend-only read-marker for tenant support tickets. Backend does not track
// which admin replies the user has already seen, so we stash a per-tenant
// map of {ticketId: lastReadIsoTimestamp} in localStorage.
import type { SupportTicketDto } from '../api/types/admin';

const READ_KEY = (tenantId: string) => `ff:support:lastRead:${tenantId}`;

type ReadMap = Record<string, string>;

function loadMap(tenantId: string): ReadMap {
  try {
    const raw = localStorage.getItem(READ_KEY(tenantId));
    return raw ? (JSON.parse(raw) as ReadMap) : {};
  } catch {
    return {};
  }
}

function saveMap(tenantId: string, map: ReadMap): void {
  try {
    localStorage.setItem(READ_KEY(tenantId), JSON.stringify(map));
  } catch {
    // Storage may be full or disabled — non-fatal.
  }
}

export function markAsRead(tenantId: string, ticketId: string): void {
  if (!tenantId || !ticketId) return;
  const map = loadMap(tenantId);
  map[ticketId] = new Date().toISOString();
  saveMap(tenantId, map);
}

export function isUnread(ticket: SupportTicketDto, tenantId: string): boolean {
  if (!ticket.id || !tenantId) return false;
  const replies = ticket.replies ?? [];
  const adminReplies = replies.filter((r) => r.fromAdmin);
  if (adminReplies.length === 0) return false;

  const lastAdminReply = adminReplies[adminReplies.length - 1];
  if (!lastAdminReply.createdAt) return false;

  const map = loadMap(tenantId);
  const lastReadIso = map[ticket.id];
  if (!lastReadIso) return true;

  return new Date(lastAdminReply.createdAt).getTime() > new Date(lastReadIso).getTime();
}
